import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { gridGalleryConfig } from '../config/gridGalleryConfig'

interface UseGridZoomOptions {
  imageCount: number
  contentOffset: number
}

interface UseGridZoomReturn {
  zoomLevelIndex: number
  maxZoomIndex: number
  columns: number
  gap: number
  zoomIn: () => void
  zoomOut: () => void
  zoomWrapperRef: React.RefObject<HTMLDivElement>
  scrollContainerRef: React.RefObject<HTMLDivElement>
}

export function useGridZoom({ imageCount, contentOffset }: UseGridZoomOptions): UseGridZoomReturn {
  const { zoomLevels, defaultZoomIndex, pinch } = gridGalleryConfig
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640

  const maxZoomIndex = (() => {
    if (typeof window === 'undefined') return zoomLevels.length - 1
    const w = window.innerWidth
    const h = window.innerHeight
    const ar = gridGalleryConfig.cellAspectRatio
    const minColumns = Math.ceil(w / (h * ar))
    for (let i = zoomLevels.length - 1; i >= 0; i--) {
      if (zoomLevels[i].columns >= minColumns) return i
    }
    return 0
  })()

  const [zoomLevelIndex, setZoomLevelIndex] = useState(
    Math.min(isMobile ? defaultZoomIndex.mobile : defaultZoomIndex.desktop, maxZoomIndex)
  )

  const zoomWrapperRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const pinchStartDistance = useRef<number | null>(null)
  const zoomCooldown = useRef(false)
  const currentLevel = zoomLevels[zoomLevelIndex]

  // Pending focal-point adjustment data — set by doZoom, consumed by useLayoutEffect
  const pendingAdjustment = useRef<{
    oldColumns: number
    newColumns: number
    oldGap: number
    newGap: number
    focalClientY: number
    oldScrollTop: number
    containerTop: number
    containerWidth: number
  } | null>(null)

  // Fallback: if zoom is triggered without a focal point (button click),
  // just preserve the current scrollTop
  const savedScrollTop = useRef<number | null>(null)

  // Track whether scroll container is mounted (for re-running effects)
  const [containerMounted, setContainerMounted] = useState(false)
  useEffect(() => {
    const check = () => {
      if (scrollContainerRef.current) {
        setContainerMounted(true)
      } else {
        requestAnimationFrame(check)
      }
    }
    check()
  }, [])

  // After React commits new column count to DOM, adjust scroll BEFORE paint.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const adj = pendingAdjustment.current
    if (adj) {
      // Focal point zoom: keep the image under the cursor in the same screen position
      pendingAdjustment.current = null

      const { oldColumns, newColumns, oldGap, newGap, focalClientY, oldScrollTop, containerTop, containerWidth } = adj
      const relativeY = focalClientY - containerTop
      const ar = gridGalleryConfig.cellAspectRatio

      // OLD layout: which grid row was at the focal point?
      const oldCellWidth = (containerWidth - Math.max(0, oldColumns - 1) * oldGap) / oldColumns
      const oldRowHeight = (oldCellWidth / ar) + oldGap
      const gridFocalY = oldScrollTop + relativeY - contentOffset
      const focalRow = Math.max(0, gridFocalY / oldRowHeight)

      const rowIndex = Math.floor(focalRow)
      const rowFraction = focalRow - rowIndex
      const imageIndex = Math.min(rowIndex * oldColumns, Math.max(0, imageCount - 1))

      // NEW layout: where is that image now?
      const newCellWidth = (containerWidth - Math.max(0, newColumns - 1) * newGap) / newColumns
      const newRowHeight = (newCellWidth / ar) + newGap
      const newRowIndex = Math.floor(imageIndex / newColumns)
      const newGridFocalY = (newRowIndex + rowFraction) * newRowHeight

      // Place that point at the same screen Y
      const newScrollTop = newGridFocalY + contentOffset - relativeY
      container.scrollTop = Math.max(0, newScrollTop)
    } else if (savedScrollTop.current !== null) {
      // Button zoom (no focal point): just keep scroll where it was
      container.scrollTop = savedScrollTop.current
      savedScrollTop.current = null
    }
  }, [zoomLevelIndex, imageCount, contentOffset])

  const doZoom = useCallback((direction: 'in' | 'out', focalClientY?: number) => {
    if (zoomCooldown.current) return
    zoomCooldown.current = true
    setTimeout(() => { zoomCooldown.current = false }, 120)

    const container = scrollContainerRef.current
    if (!container) return

    if (focalClientY !== undefined) {
      // Focal point zoom (pinch / ctrl+scroll)
      const rect = container.getBoundingClientRect()
      setZoomLevelIndex(prev => {
        const next = direction === 'in'
          ? Math.min(prev + 1, maxZoomIndex)
          : Math.max(prev - 1, 0)
        if (next === prev) return prev

        pendingAdjustment.current = {
          oldColumns: zoomLevels[prev].columns,
          newColumns: zoomLevels[next].columns,
          oldGap: zoomLevels[prev].gap,
          newGap: zoomLevels[next].gap,
          focalClientY,
          oldScrollTop: container.scrollTop,
          containerTop: rect.top,
          containerWidth: container.clientWidth,
        }
        return next
      })
    } else {
      // Button zoom (no focal point) — just preserve scroll position
      savedScrollTop.current = container.scrollTop
      setZoomLevelIndex(prev => {
        const next = direction === 'in'
          ? Math.min(prev + 1, maxZoomIndex)
          : Math.max(prev - 1, 0)
        return next
      })
    }
  }, [zoomLevels, maxZoomIndex])

  // Ctrl+wheel / trackpad pinch on scroll container
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return
      e.preventDefault()
      if (e.deltaY < 0) {
        doZoom('in', e.clientY)
      } else if (e.deltaY > 0) {
        doZoom('out', e.clientY)
      }
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  }, [doZoom, containerMounted])

  // Mobile: Pinch-to-zoom — attached to scroll container (where touches land)
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const getDistance = (t1: Touch, t2: Touch) => {
      const dx = t1.clientX - t2.clientX
      const dy = t1.clientY - t2.clientY
      return Math.sqrt(dx * dx + dy * dy)
    }

    const getCenter = (t1: Touch, t2: Touch) => ({
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2,
    })

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        pinchStartDistance.current = getDistance(e.touches[0], e.touches[1])
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchStartDistance.current !== null) {
        e.preventDefault()
        const newDistance = getDistance(e.touches[0], e.touches[1])
        const ratio = newDistance / pinchStartDistance.current
        const center = getCenter(e.touches[0], e.touches[1])

        if (ratio > pinch.zoomInRatio) {
          doZoom('in', center.y)
          pinchStartDistance.current = newDistance
        } else if (ratio < pinch.zoomOutRatio) {
          doZoom('out', center.y)
          pinchStartDistance.current = newDistance
        }
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        pinchStartDistance.current = null
      }
    }

    // Block Safari's native gesture-based zoom (separate from touch events)
    const blockGesture = (e: Event) => e.preventDefault()

    container.addEventListener('touchstart', handleTouchStart, { passive: false })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd)
    container.addEventListener('gesturestart', blockGesture, { passive: false } as any)
    container.addEventListener('gesturechange', blockGesture, { passive: false } as any)

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('gesturestart', blockGesture)
      container.removeEventListener('gesturechange', blockGesture)
    }
  }, [doZoom, pinch.zoomInRatio, pinch.zoomOutRatio, containerMounted])

  return {
    zoomLevelIndex,
    maxZoomIndex,
    columns: currentLevel.columns,
    gap: currentLevel.gap,
    zoomIn: () => doZoom('in'),
    zoomOut: () => doZoom('out'),
    zoomWrapperRef: zoomWrapperRef as React.RefObject<HTMLDivElement>,
    scrollContainerRef: scrollContainerRef as React.RefObject<HTMLDivElement>,
  }
}
